import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Patient } from '../../types/types';
import { useAccessToken } from '@features/user';
import APIService from '../../services/APIService';
import { NextAppointmentCard } from '@features/appointments';
import { CalendarMonth, FiberManualRecord } from '@mui/icons-material';
import BaseBlock from './Blocks/BaseBlock';
import { AddAppointmentModal } from '@features/appointments';
import { useGetNextAppointmentQuery } from '@features/appointments';
import { useAppointmentTypes } from '@features/appointment-types';
import { useCreateEncounterMutation } from '@features/encounters';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

interface PatientNextAppointmentProps {
  patientId: string;
}

/**
 * Component that fetches and displays the next appointment for a patient
 */
const PatientNextAppointment: React.FC<PatientNextAppointmentProps> = ({ patientId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [patient, setPatient] = useState<Patient | undefined>(undefined);
  const { getAccessToken } = useAccessToken();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [createEncounter, { isLoading: isCreatingEncounter }] = useCreateEncounterMutation();
  const { appointmentTypes } = useAppointmentTypes();

  const { data: appointmentsData, isLoading } = useGetNextAppointmentQuery({
    patientId,
    num: 1,
  });

  const nextAppointment = appointmentsData?.items?.[0] || null;

  useEffect(() => {
    const fetchPatientData = async () => {
      if (!patientId) return;

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          console.error('Failed to get access token');
          return;
        }

        const response = await APIService.makeAPIGetRequest({
          requestString: `/patient/get?patient_id=${patientId}`,
          accessToken,
        });

        if (response.ok) {
          setPatient(response.value.patient);
        }
      } catch (error) {
        console.error('Error fetching patient data:', error);
      }
    };

    fetchPatientData();
  }, [patientId, getAccessToken]);

  const handleScheduleAppointment = () => {
    setModalOpen(true);
  };

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleRecordNow = useCallback(async () => {
    try {
      const defaultAppointmentType = appointmentTypes?.[0];

      interface CreateRequest {
        patient_id: string;
        apply_defaults?: boolean;
        encounter_type?: string;
        template_id?: string;
      }

      const createRequest: CreateRequest = {
        patient_id: patientId,
        apply_defaults: true,
      };

      if (defaultAppointmentType) {
        createRequest.encounter_type = defaultAppointmentType.encounter_type;
        createRequest.template_id = defaultAppointmentType.template_id ?? undefined;
      }

      const newEncounter = await createEncounter({
        createEncounterRequest: createRequest,
      }).unwrap();

      // Navigate to the newly created encounter
      navigate(`/encounters/${newEncounter.encounter_id}`);
    } catch (error) {
      console.error('Error creating encounter:', error);
      enqueueSnackbar('Failed to create encounter', { variant: 'error' });
    }
  }, [patientId, appointmentTypes, createEncounter, navigate, enqueueSnackbar]);

  if (isLoading) {
    return (
      <Box>
        <NextAppointmentCard
          isLoading={true}
          showPatientName={false}
          title="UPCOMING APPOINTMENT"
        />
      </Box>
    );
  }

  if (!nextAppointment) {
    return (
      <>
        <BaseBlock>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarMonth sx={{ color: 'text.secondary' }} />
              <Typography variant="body1" color="text.secondary">
                No upcoming appointment
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRecordNow}
                disabled={isCreatingEncounter}
                startIcon={<FiberManualRecord />}
              >
                Record Now
              </Button>
              <Button variant="outlined" color="primary" onClick={handleScheduleAppointment}>
                Schedule Appointment
              </Button>
            </Box>
          </Box>
        </BaseBlock>

        <AddAppointmentModal open={modalOpen} onClose={handleModalClose} initialPatient={patient} />
      </>
    );
  }

  return (
    <>
      <Box>
        <NextAppointmentCard
          appointment={nextAppointment}
          showPatientName={false}
          title="UPCOMING APPOINTMENT"
        />
      </Box>

      <AddAppointmentModal open={modalOpen} onClose={handleModalClose} initialPatient={patient} />
    </>
  );
};

export default PatientNextAppointment;
